
// export default Header;
import React, { useState, useEffect } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "../../contexts/CartContext";
import { useAuth } from "../../contexts/AuthContext";
import { useFavorites } from "../../contexts/FavoritesContext";
import CartSidebar from "../checkout/CartSidebar";

const Header = () => {
  const { cartItems } = useCart();
  const { user, signOut } = useAuth();
  const { getFavoriteCount } = useFavorites();
  const wishlistCount = getFavoriteCount();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const isMobileNavActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const mobileNavPillClass = (active) =>
    `inline-flex w-auto items-center rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-all ${
      active
        ? "border border-amber-400/70 bg-amber-400/20 text-amber-900 shadow-[0_0_12px_rgba(251,191,36,0.15)]"
        : "border border-amber-200/50 bg-white/70 text-slate-700 hover:border-amber-300/70 hover:bg-amber-50/90 hover:text-amber-900"
    }`;

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      toast.error("Could not sign out. Please try again.");
      console.error("Error signing out:", error);
    }
  };

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isMenuOpen]);

  const navClassName = ({ isActive }) =>
    `text-[11px] sm:text-xs font-semibold uppercase tracking-wider transition-colors ${
      isActive ? "text-amber-400" : "text-amber-200 hover:text-amber-400"
    }`;

  return (
    <header className="bg-gradient-to-r from-slate-900 via-gray-900 to-slate-900 text-white shadow-2xl sticky top-0 z-50 border-b border-amber-400/20">
      <div className="relative container mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" aria-label="Go to JmoLux homepage" className="flex items-center">
          <img
            src="/img/Logo_Directory/crest_gold_real_transparent.png"
            alt="JmoLux Crest"
            className="h-14 md:h-16 w-auto object-contain"
          />
        </Link>
        {/* Mobile-only status — desktop uses nav auth actions instead */}
        {user ? (
          <div className="flex items-center space-x-2 md:hidden">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-amber-200 text-xs sm:text-sm font-medium">
              <span className="sm:hidden">Online</span>
              <span className="hidden sm:inline">
                {user.user_metadata?.first_name || user.email?.split('@')[0]}
              </span>
            </span>
          </div>
        ) : (
          <div className="flex items-center space-x-2 md:hidden">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span className="text-amber-200/60 text-xs sm:text-sm">Guest</span>
          </div>
        )}

        <nav className="hidden md:flex space-x-8">
          <NavLink to="/" end className={navClassName}>Home</NavLink>
          <NavLink to="/shop" className={navClassName}>Shop</NavLink>
          <NavLink to="/collections" className={navClassName}>Collections</NavLink>
          {user && (
            <NavLink to="/orders" className={navClassName}>My Orders</NavLink>
          )}
          <NavLink to="/wishlist" className={navClassName}>Wishlist</NavLink>
          <NavLink to="/about" className={navClassName}>About</NavLink>
          <NavLink to="/contact" className={navClassName}>Contact</NavLink>
        </nav>
        <div className="flex shrink-0 items-center space-x-3 sm:space-x-4">
          <button className="hidden text-amber-200 hover:text-amber-400 md:inline-flex">{/* Search SVG */}</button>
          <button
            type="button"
            onClick={() => navigate("/wishlist")}
            className="relative flex h-9 w-9 items-center justify-center rounded-full text-amber-200 transition-colors hover:bg-amber-400/10 hover:text-amber-400"
            title="Wishlist"
            aria-label={wishlistCount > 0 ? `Open wishlist, ${wishlistCount} items` : "Open wishlist"}
          >
            <svg
              aria-hidden="true"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"
              />
            </svg>
            {wishlistCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[1.125rem] rounded-full bg-amber-400 px-1 text-center text-[10px] font-bold leading-4 text-black">
                {wishlistCount}
              </span>
            )}
          </button>
          {user ? (
            <button onClick={handleSignOut} className="hidden md:inline text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-amber-200 hover:text-white border border-amber-400/30 px-3 py-1.5 rounded-full transition-colors">Logout</button>
          ) : (
            <div className="hidden md:flex items-center space-x-2">
              <Link to="/login" className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-amber-200 hover:text-amber-400 transition-colors">Login</Link>
              <Link to="/signup" className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-amber-200 hover:text-white border border-amber-400/30 px-3 py-1.5 rounded-full transition-colors">Sign Up</Link>
            </div>
          )}
          <button onClick={() => setIsCartOpen(!isCartOpen)} className="relative text-amber-200 hover:text-amber-400">
            🛒
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-amber-400 text-black text-xs px-2 rounded-full">{cartCount}</span>
            )}
          </button>
          <button
            type="button"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
            className="md:hidden relative flex h-11 w-11 items-center justify-center rounded-full border border-amber-400/25 bg-white/5 text-amber-100 shadow-[0_0_0_1px_rgba(255,255,255,0.03)] backdrop-blur-sm transition-all hover:border-amber-300/40 hover:bg-white/10"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span className="absolute inset-[5px] rounded-full bg-gradient-to-br from-amber-400/10 via-transparent to-white/5"></span>
            <span className="relative flex h-4 w-4 flex-col items-center justify-center">
              <span
                className={`absolute h-[1.5px] w-4 rounded-full bg-current transition-all duration-300 ${
                  isMenuOpen ? "translate-y-0 rotate-45" : "-translate-y-[5px]"
                }`}
              ></span>
              <span
                className={`absolute h-[1.5px] rounded-full bg-current transition-all duration-300 ${
                  isMenuOpen ? "w-0 opacity-0" : "w-4 opacity-100"
                }`}
              ></span>
              <span
                className={`absolute h-[1.5px] w-4 rounded-full bg-current transition-all duration-300 ${
                  isMenuOpen ? "translate-y-0 -rotate-45" : "translate-y-[5px]"
                }`}
              ></span>
            </span>
          </button>
        </div>
      </div>

      {/* Mobile drawer — full-height slide-in from right */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Drawer panel */}
          <div className="relative ml-auto flex h-full w-4/5 max-w-xs flex-col border-l border-amber-300/70 bg-[#fffdf8] text-slate-900 shadow-2xl">

            {/* Top bar */}
            <div className="flex items-center justify-between border-b border-amber-200 bg-[#fff8e7] px-5 py-4">
              <img
                src="/img/Logo_Directory/crest_gold_real_transparent.png"
                alt="JmoLux"
                className="h-10 w-auto object-contain"
              />
              <button
                onClick={() => setIsMenuOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-amber-400/70 bg-white text-slate-900 shadow-sm transition-colors hover:bg-amber-100 hover:text-amber-800"
                aria-label="Close menu"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* User status pill */}
            <div className="mx-5 mt-5 flex flex-col items-start">
              <div className="inline-flex w-auto items-center gap-2 rounded-full border border-amber-300/50 bg-amber-50/80 px-4 py-2 shadow-sm">
                <div className={`h-2 w-2 rounded-full ${user ? "bg-green-400" : "bg-red-400"}`} />
                <span className="max-w-[180px] truncate text-xs font-semibold uppercase tracking-wider text-slate-800">
                  {user ? (user.user_metadata?.first_name || user.email?.split("@")[0] || "Online") : "Guest"}
                </span>
              </div>
            </div>

            {/* Nav links */}
            <nav className="flex flex-1 flex-col items-start gap-2.5 overflow-y-auto px-5 py-5">
              {[
                { label: "Home", path: "/" },
                { label: "Shop", path: "/shop" },
                { label: "Collections", path: "/collections" },
                { label: "About", path: "/about" },
                { label: "Contact", path: "/contact" },
                ...(user ? [{ label: "My Orders", path: "/orders" }] : []),
                { label: "Wishlist", path: "/wishlist" },
              ].map(({ label, path }) => (
                <button
                  key={path}
                  type="button"
                  onClick={() => { navigate(path); setIsMenuOpen(false); }}
                  className={`${mobileNavPillClass(isMobileNavActive(path))}${path === "/wishlist" && wishlistCount > 0 ? " gap-2" : ""}`}
                >
                  {label}
                  {path === "/wishlist" && wishlistCount > 0 && (
                    <span className="rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold leading-none text-black">
                      {wishlistCount}
                    </span>
                  )}
                </button>
              ))}

              <button
                type="button"
                onClick={() => { setIsCartOpen(true); setIsMenuOpen(false); }}
                className={`${mobileNavPillClass(false)} gap-2`}
              >
                Cart
                {cartCount > 0 && (
                  <span className="rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold leading-none text-black">
                    {cartCount}
                  </span>
                )}
              </button>
            </nav>

            {/* Auth footer */}
            <div className="flex flex-col items-start gap-2.5 border-t border-amber-200/60 px-5 pb-8 pt-4">
              {!user ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to="/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="inline-flex w-auto rounded-full border border-amber-300/60 bg-white/80 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 transition-colors hover:border-amber-400/70 hover:bg-amber-50 hover:text-amber-900"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setIsMenuOpen(false)}
                    className="inline-flex w-auto rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-950 shadow-sm transition-all hover:from-amber-300 hover:to-amber-400"
                  >
                    Sign Up
                  </Link>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => { handleSignOut(); setIsMenuOpen(false); }}
                  className="inline-flex w-auto rounded-full border border-red-200/60 bg-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-red-700 transition-colors hover:border-red-300/70 hover:bg-red-50/80"
                >
                  Logout
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {isCartOpen && <CartSidebar onClose={() => setIsCartOpen(false)} />}
    </header>
  );
};

export default Header;
